import { useState, useEffect } from "react";
import { tr } from "@/utils/i18n";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, Mail } from "lucide-react";
import api from "@/utils/api";
import { useLanguage } from "@/context/LanguageContext";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  
  const token = searchParams.get("token");
  
  const [mode, setMode] = useState(token ? "reset" : "request"); // "request" or "reset"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [demoToken, setDemoToken] = useState(null);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSuccess(true);
      if (response.data.demo_token) {
        setDemoToken(response.data.demo_token);
      }
      toast.success(tr(language, 'Email sent!', 'Email enviado!', '¡Email enviado!'));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(tr(language, 'Passwords do not match', 'Senhas não coincidem', 'Las contraseñas no coinciden'));
      return;
    }
    
    if (password.length < 8) {
      toast.error(tr(language, 'Password must be at least 8 characters', 'Senha deve ter pelo menos 8 caracteres', 'La contraseña debe tener al menos 8 caracteres'));
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { 
        token: token,
        new_password: password 
      });
      setSuccess(true);
      toast.success(tr(language, 'Password reset successfully!', 'Senha redefinida com sucesso!', '¡Contraseña restablecida!'));
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-950/50 border-zinc-800">
        <CardHeader className="text-center">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="absolute left-4 top-4 text-zinc-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
            {success ? (
              <CheckCircle className="w-8 h-8 text-green-400" />
            ) : (
              <Lock className="w-8 h-8 text-cyan-400" />
            )}
          </div>
          <CardTitle className="text-2xl text-white">
            {mode === "request" ? (
              tr(language, 'Forgot Password?', 'Esqueceu a Senha?', '¿Olvidaste tu Contraseña?')
            ) : (
              tr(language, 'Reset Password', 'Redefinir Senha', 'Restablecer Contraseña')
            )}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {mode === "request" ? (
            success ? (
              <div className="text-center space-y-4">
                <p className="text-zinc-400">
                  {tr(language, "If this email exists in our system, you will receive a link to reset your password.", "Se o email existir em nosso sistema, você receberá um link para redefinir sua senha.", "Si el correo existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña.")}
                </p>
                
                {demoToken && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-400 text-sm font-medium mb-2">
                      {tr(language, '⚠️ Demo Mode - Email not configured', '⚠️ Modo Demo - Email não configurado', '⚠️ Modo Demo - Email no configurado')}
                    </p>
                    <p className="text-xs text-zinc-400 mb-2">
                      {tr(language, 'Use this link to reset:', 'Use este link para redefinir:', 'Usa este enlace para restablecer:')}
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => navigate(`/reset-password?token=${demoToken}`)}
                    >
                      {tr(language, 'Go to Reset', 'Ir para Redefinição', 'Ir a Restablecer')}
                    </Button>
                  </div>
                )}
                
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/')}
                  className="mt-4"
                >
                  {tr(language, 'Back to Login', 'Voltar ao Login', 'Volver al Inicio')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                      required
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={loading}
                  data-testid="send-reset-btn"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5" />
                  ) : (
                    tr(language, 'Send Reset Link', 'Enviar Link de Redefinição', 'Enviar Enlace de Restablecimiento')
                  )}
                </Button>
                
                <p className="text-center text-sm text-zinc-500">
                  {tr(language, 'Remember your password?', 'Lembrou a senha?', '¿Recordaste tu contraseña?')}{' '}
                  <button 
                    type="button"
                    onClick={() => navigate('/')} 
                    className="text-cyan-400 hover:underline"
                  >
                    {tr(language, 'Sign In', 'Entrar', 'Iniciar Sesión')}
                  </button>
                </p>
              </form>
            )
          ) : (
            success ? (
              <div className="text-center space-y-4">
                <p className="text-green-400">
                  {tr(language, 'Password reset successfully!', 'Senha redefinida com sucesso!', '¡Contraseña restablecida con éxito!')}
                </p>
                <p className="text-zinc-400 text-sm">
                  {tr(language, 'Redirecting to login...', 'Redirecionando para o login...', 'Redirigiendo al inicio de sesión...')}
                </p>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">
                    {tr(language, 'New Password', 'Nova Senha', 'Nueva Contraseña')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white"
                      required
                      minLength={8}
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-400">
                    {tr(language, 'Confirm Password', 'Confirmar Senha', 'Confirmar Contraseña')}
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 bg-zinc-900 border-zinc-700 text-white"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={loading}
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <div className="spinner w-5 h-5" />
                  ) : (
                    tr(language, 'Reset Password', 'Redefinir Senha', 'Restablecer Contraseña')
                  )}
                </Button>
              </form>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
}
